import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';

// Route permissions configuration - Single source of truth
const ROUTE_PERMISSIONS = {
  // Admin has access to all routes
  admin: ['*'],
  
  // Manager has access to specific routes
  manager: [
    'services',
    'dispatch-queue',
    'disputes',
    'case-booking',
    'add-service',
    'create-case',
    'vendor-search',
    'invoices',
    'case-history',
    'referrals',
    'users',
    'vendor-registrations',
    'reports',
  ],
  
  // Agent has same access as manager for now
  agent: [
    'services',
    'dispatch-queue',
    'disputes',
    'case-booking',
    'add-service',
    'create-case',
    'vendor-search',
    'case-history',
  ],
  
  // No access roles - will be redirected to login
  vendor_owner: [],
  vendor_manager: [],
  driver: [],
};

// Default landing pages for each role after authentication
const DEFAULT_ROUTES = {
  admin: '/services',
  manager: '/services', 
  agent: '/services',
  vendor_owner: '/login',
  vendor_manager: '/login',
  driver: '/login',
};

/**
 * Get default route for a user role
 * @param {string} userRole - User's role
 * @returns {string} Default route path
 */
export const getDefaultRoute = (userRole) => {
  return DEFAULT_ROUTES[userRole?.toLowerCase()] || '/login';
};

/**
 * Extract route name from pathname for permission checking
 * @param {string} pathname - Current route pathname
 * @returns {string} Route name for permission checking
 */
const getRouteNameFromPath = (pathname) => {
  // Remove leading slash and get the first part of the path
  const cleanPath = pathname.replace(/^\//, '');
  
  // Handle nested routes
  if (cleanPath.startsWith('maintenance/')) {
    // For maintenance routes, return the specific sub-route
    const subRoute = cleanPath.replace('maintenance/', '');
    return subRoute || 'maintenance';
  }
  
  // For regular routes, return the first segment
  const firstSegment = cleanPath.split('/')[0];
  return firstSegment || 'dashboard';
};

/**
 * Enhanced PrivateRoute component with role-based access control
 * Automatically determines permissions based on current route
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.redirectTo - Where to redirect if access is denied (default: /login)
 * @param {string} props.overrideRoute - Override automatic route detection
 */
const RoleBasedRoute = ({ 
  children, 
  redirectTo = '/login',
  overrideRoute = null
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Get user role (normalize to lowercase)
  const userRole = user?.role?.toLowerCase();
  
  // If no user role found, deny access
  if (!userRole) {
    console.warn('User role not found, denying access');
    return <Navigate to={redirectTo} />;
  }

  // Determine the route to check permissions for
  const routeToCheck = overrideRoute || getRouteNameFromPath(location.pathname);
  
  // Check if user has access based on role and current route
  const hasAccess = checkRoleAccess(userRole, routeToCheck);
  
  if (!hasAccess) {
    console.warn(`Access denied for role: ${userRole}, route: ${routeToCheck}, path: ${location.pathname}`);
    return <Navigate to={redirectTo} />;
  }

  return children;
};

/**
 * Check if a user role has access to a specific route
 * @param {string} userRole - Current user's role
 * @param {string} routeName - Route name to check
 * @returns {boolean} Whether access is granted
 */
const checkRoleAccess = (userRole, routeName) => {
  // Get user permissions for the role
  const userPermissions = ROUTE_PERMISSIONS[userRole] || [];
  
  // Admin has wildcard access
  if (userPermissions.includes('*')) {
    return true;
  }
  
  // Check if user has permission for the specific route
  return userPermissions.includes(routeName);
};

export default RoleBasedRoute; 